import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { getProfitabilityOverview, getMonthlyProfitability } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PERIOD_KEYS = {
  MONTH: 'MONTH',
  QUARTER: 'QUARTER',
  YEAR: 'YEAR',
  ALL: 'ALL'
};

const Profitability = () => {
  const { t } = useLanguage();
  const texts = t('profitability');

  const [selectedPeriodKey, setSelectedPeriodKey] = useState(PERIOD_KEYS.MONTH);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [error, setError] = useState(null);

  const periodOptions = useMemo(() => ([
    { key: PERIOD_KEYS.MONTH, label: texts.thisMonth || 'This Month' },
    { key: PERIOD_KEYS.QUARTER, label: texts.thisQuarter || 'This Quarter' },
    { key: PERIOD_KEYS.YEAR, label: texts.thisYear || 'This Year' },
    { key: PERIOD_KEYS.ALL, label: texts.allTime || 'All' }
  ]), [texts.thisMonth, texts.thisQuarter, texts.thisYear, texts.allTime]);

  const fetchKeyRef = useRef(null);
  const isFetchingRef = useRef(false);

  const getBusinessId = useCallback(() => {
    const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
    return selectedBusiness?.id;
  }, []);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    const num = Number(amount);
    const formatted = Math.abs(num).toLocaleString('en-IN', { maximumFractionDigits: 0 });
    return num < 0 ? `-₹${formatted}` : `₹${formatted}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0%';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${Number(value).toFixed(1)}%`;
  };

  const comparisonLabel = useMemo(() => {
    if (selectedPeriodKey === PERIOD_KEYS.QUARTER) return texts.fromLastQuarter || 'from last quarter';
    if (selectedPeriodKey === PERIOD_KEYS.YEAR) return texts.fromLastYear || 'from last year';
    if (selectedPeriodKey === PERIOD_KEYS.ALL) return texts.fromPriorPeriod || 'vs prior period';
    return texts.fromLastMonth || 'from last month';
  }, [selectedPeriodKey, texts]);

  const loadData = useCallback(async () => {
    const businessId = getBusinessId();
    if (!businessId) {
      setError('No business selected. Please select a business first.');
      setLoading(false);
      return;
    }

    const yearParam = selectedPeriodKey === PERIOD_KEYS.YEAR ? selectedYear : null;
    const fetchKey = `${selectedPeriodKey}-${yearParam || 'na'}`;
    if (isFetchingRef.current || fetchKeyRef.current === fetchKey) {
      return;
    }

    isFetchingRef.current = true;
    fetchKeyRef.current = fetchKey;

    try {
      setLoading(true);
      setError(null);

      const [overviewResponse, monthlyResponse] = await Promise.all([
        getProfitabilityOverview(businessId, selectedPeriodKey, yearParam),
        getMonthlyProfitability(businessId, null, yearParam, selectedPeriodKey)
      ]);

      if (overviewResponse.success) {
        setOverviewData(overviewResponse.data);
        if (overviewResponse.data?.availableYears?.length) {
          setAvailableYears(overviewResponse.data.availableYears);
        }
      } else {
        setError(overviewResponse.message || 'Failed to fetch profitability data');
      }

      if (monthlyResponse.success) {
        setMonthlyData(monthlyResponse.data);
        if (monthlyResponse.data?.availableYears?.length) {
          setAvailableYears(monthlyResponse.data.availableYears);
        }
      }
    } catch (err) {
      setError('An error occurred while fetching profitability data');
      console.error('Error loading profitability data:', err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [getBusinessId, selectedPeriodKey, selectedYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const chartBreakdown = monthlyData?.monthlyBreakdown || overviewData?.monthlyBreakdown || [];
  const overviewChartBreakdown = overviewData?.monthlyBreakdown || chartBreakdown;
  const financialPosition = monthlyData?.financialPosition;
  const profitInsights = monthlyData?.profitInsights;
  const financialTable = monthlyData?.monthlyFinancialTable || [];

  const getMetrics = () => {
    const currentMonth = monthlyData?.currentMonth;
    if (!currentMonth) {
      return [
        { title: texts.grossIncome || 'Gross Income', value: '₹0', subtitle: 'No data available', color: 'green' },
        { title: texts.totalExpenses || 'Total Expenses', value: '₹0', subtitle: 'No data available', color: 'red' },
        { title: texts.netProfit || 'Net Profit', value: '₹0', subtitle: 'No data available', color: 'blue' }
      ];
    }

    const netProfit = Number(currentMonth.netProfit || 0);
    return [
      {
        title: texts.grossIncome || 'Gross Income',
        value: formatCurrency(currentMonth.grossIncome),
        subtitle: `${formatPercentage(currentMonth.grossIncomeChange)} ${comparisonLabel}`,
        color: 'green',
        change: currentMonth.grossIncomeChange
      },
      {
        title: texts.totalExpenses || 'Total Expenses',
        value: formatCurrency(currentMonth.totalExpenses),
        subtitle: `${formatPercentage(currentMonth.totalExpensesChange)} ${comparisonLabel}`,
        color: 'red',
        change: currentMonth.totalExpensesChange
      },
      {
        title: netProfit < 0 ? (texts.netLoss || 'Net Loss') : (texts.netProfit || 'Net Profit'),
        value: formatCurrency(netProfit),
        subtitle: `${formatPercentage(currentMonth.netProfitChange)} ${comparisonLabel}`,
        color: netProfit < 0 ? 'red' : 'blue',
        change: currentMonth.netProfitChange
      }
    ];
  };

  const getMonthlyBreakdownChartData = () => {
    if (!chartBreakdown.length) return null;
    return {
      labels: chartBreakdown.map((item) => item.monthName || `${item.month}/${item.year}`),
      datasets: [
        {
          label: texts.grossIncome || 'Gross Income',
          data: chartBreakdown.map((item) => item.grossIncome || 0),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: texts.totalExpenses || 'Total Expenses',
          data: chartBreakdown.map((item) => item.totalExpenses || 0),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: texts.netProfit || 'Net Profit',
          data: chartBreakdown.map((item) => item.netProfit || 0),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  };

  const getOverviewChartData = () => {
    if (!overviewChartBreakdown.length) return null;
    return {
      labels: overviewChartBreakdown.map((item) => item.monthName || item.month || 'Unknown'),
      datasets: [
        {
          label: texts.interestEarned || 'Interest Earned',
          data: overviewChartBreakdown.map((item) => item.interestEarned || item.grossIncome || 0),
          backgroundColor: 'rgba(34, 197, 94, 0.8)'
        },
        {
          label: texts.pledgeInterestPaid || 'Bank Interest Paid',
          data: overviewChartBreakdown.map((item) => item.pledgeInterestPaid || item.totalExpenses || 0),
          backgroundColor: 'rgba(239, 68, 68, 0.8)'
        },
        {
          label: texts.netProfit || 'Net Profit',
          data: overviewChartBreakdown.map((item) => item.netProfit || 0),
          backgroundColor: 'rgba(59, 130, 246, 0.8)'
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`
        }
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => {
            if (value >= 1000000) return `₹${(value / 1000000).toFixed(1)}M`;
            if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
            return `₹${value}`;
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Loader2 className="spin" size={48} color="#3b82f6" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</div>
        <button
          onClick={() => {
            fetchKeyRef.current = null;
            isFetchingRef.current = false;
            loadData();
          }}
          className="btn btn-primary"
        >
          Retry
        </button>
      </div>
    );
  }

  const metrics = getMetrics();
  const monthlyChartData = getMonthlyBreakdownChartData();
  const overviewChartData = getOverviewChartData();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div className="segmented-control">
          {periodOptions.map((period) => (
            <button
              key={period.key}
              className={`segmented-tab ${selectedPeriodKey === period.key ? 'active' : ''}`}
              onClick={() => {
                fetchKeyRef.current = null;
                isFetchingRef.current = false;
                setSelectedPeriodKey(period.key);
              }}
            >
              {period.label}
            </button>
          ))}
        </div>

        {selectedPeriodKey === PERIOD_KEYS.YEAR && (
          <select
            className="form-select"
            value={selectedYear}
            onChange={(e) => {
              fetchKeyRef.current = null;
              isFetchingRef.current = false;
              setSelectedYear(Number(e.target.value));
            }}
            style={{ width: 'auto' }}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', margin: '1.5rem 0' }}>
        {metrics.map((metric, index) => {
          const isPositive = metric.change === undefined || metric.change >= 0;
          return (
            <div
              key={index}
              className={`kpi-card ${metric.color}`}
              style={{ borderLeft: `4px solid ${metric.color === 'green' ? '#10b981' : metric.color === 'red' ? '#ef4444' : '#3b82f6'}` }}
            >
              <div className="kpi-value" style={{ color: metric.color === 'green' ? '#10b981' : metric.color === 'red' ? '#ef4444' : '#3b82f6' }}>
                {metric.value}
              </div>
              <div className="kpi-label">{metric.title}</div>
              <div className="kpi-subtitle" style={{ color: isPositive ? '#10b981' : '#ef4444' }}>
                {metric.subtitle}
              </div>
            </div>
          );
        })}
      </div>

      {financialPosition && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 className="card-title profit-section-title">{texts.financialPosition || 'Financial Position'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {[
              { label: texts.moneyWithCustomers || 'Money with Customers', value: financialPosition.moneyWithCustomers },
              { label: texts.moneyBorrowedFromBank || 'Money Borrowed from Bank', value: financialPosition.moneyBorrowedFromBank },
              { label: texts.ownCapitalInvested || 'Own Capital Invested', value: financialPosition.ownCapitalInvested },
              { label: texts.outstandingInterestYetToCollect || 'Outstanding Interest Yet to Collect', value: financialPosition.outstandingInterestYetToCollect }
            ].map((item) => (
              <div key={item.label} className="theme-panel" style={{ padding: '1rem' }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>{item.label}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>{formatCurrency(item.value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {profitInsights && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 className="card-title profit-section-title">{texts.profitInsights || 'Profit Insights'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div className="theme-panel-soft" style={{ padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{texts.highestProfitMonth || 'Highest Profit Month'}</div>
              <div style={{ fontWeight: 600, marginTop: '0.25rem' }}>{profitInsights.highestProfitMonth || '-'}</div>
              <div style={{ color: '#059669' }}>{formatCurrency(profitInsights.highestProfitAmount)}</div>
            </div>
            <div className="theme-panel-soft" style={{ padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{texts.lowestProfitMonth || 'Lowest Profit Month'}</div>
              <div style={{ fontWeight: 600, marginTop: '0.25rem' }}>{profitInsights.lowestProfitMonth || '-'}</div>
              <div style={{ color: '#dc2626' }}>{formatCurrency(profitInsights.lowestProfitAmount)}</div>
            </div>
            <div className="theme-panel-soft" style={{ padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{texts.averageMonthlyProfit || 'Average Monthly Profit'}</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: '0.25rem' }}>{formatCurrency(profitInsights.averageMonthlyProfit)}</div>
            </div>
            <div className="theme-panel-soft" style={{ padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{texts.totalProfitSelected || 'Total Profit (Selected Period)'}</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: '0.25rem', color: '#3b82f6' }}>{formatCurrency(profitInsights.totalProfit)}</div>
            </div>
          </div>
        </div>
      )}

      {monthlyChartData && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 className="card-title profit-section-title">{texts.monthlyProfitabilityTrend || 'Monthly Profitability Trend'}</h3>
          <div style={{ height: '360px', position: 'relative' }}>
            <Line data={monthlyChartData} options={chartOptions} />
          </div>
        </div>
      )}

      {overviewChartData && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 className="card-title profit-section-title">{texts.monthlyBreakdownOverview || 'Monthly Breakdown Overview'}</h3>
          <div style={{ height: '360px', position: 'relative' }}>
            <Bar data={overviewChartData} options={chartOptions} />
          </div>
        </div>
      )}

      {financialTable.length > 0 && (
        <div className="card">
          <h3 className="card-title profit-section-title">{texts.monthlyFinancialTable || 'Monthly Financial Table'}</h3>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{texts.month || 'Month'}</th>
                  <th>{texts.interestEarned || 'Interest Earned'}</th>
                  <th>{texts.bankInterestPaid || 'Bank Interest Paid'}</th>
                  <th>{texts.netProfit || 'Net Profit'}</th>
                  <th>{texts.profitMargin || 'Profit Margin %'}</th>
                </tr>
              </thead>
              <tbody>
                {financialTable.map((row) => (
                  <tr key={`${row.year}-${row.month}`}>
                    <td>{row.monthName}</td>
                    <td>{formatCurrency(row.interestEarned)}</td>
                    <td>{formatCurrency(row.bankInterestPaid)}</td>
                    <td style={{ color: Number(row.netProfit) < 0 ? '#dc2626' : '#059669', fontWeight: 500 }}>
                      {formatCurrency(row.netProfit)}
                    </td>
                    <td>{Number(row.profitMarginPercent || 0).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profitability;
